/**
 * Central registry of TanStack Query keys (SPEC.md §10.2).
 *
 * Keys are structured and built here rather than inlined at call sites, so a mutation can
 * invalidate exactly the affected slice — `queryClient.invalidateQueries()` with no argument is
 * forbidden by the spec.
 *
 * @example
 * queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
 */
import type { TableQuery } from '@/shared/composables/useTableState'

export const queryKeys = {
  health: () => ['health'] as const,
  auth: {
    me: () => ['auth', 'me'] as const,
    sessions: () => ['auth', 'sessions'] as const,
  },
  users: {
    all: () => ['users'] as const,
    list: (filters: TableQuery) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  units: {
    all: () => ['units'] as const,
  },
  settings: {
    expiry: () => ['settings', 'expiry'] as const,
    field: () => ['settings', 'field'] as const,
    all: () => ['settings'] as const,
  },
  categories: {
    all: () => ['categories'] as const,
  },
  warehouses: {
    all: () => ['warehouses'] as const,
    locations: (warehouseId: string) => ['warehouses', 'locations', warehouseId] as const,
  },
  currencies: {
    all: () => ['currencies'] as const,
    rates: (currencyId: string) => ['currencies', 'rates', currencyId] as const,
  },
  products: {
    all: () => ['products'] as const,
    list: (filters: TableQuery) => ['products', 'list', filters] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
  },
  suppliers: {
    all: () => ['suppliers'] as const,
    list: (filters: TableQuery) => ['suppliers', 'list', filters] as const,
  },
  agents: {
    kpi: (id: string, year: number) => ['agents', 'kpi', id, year] as const,
    assignments: (id: string) => ['agents', id, 'assignments'] as const,
    all: () => ['agents'] as const,
    list: (filters: TableQuery) => ['agents', 'list', filters] as const,
  },
  receipts: {
    all: () => ['receipts'] as const,
    list: (filters: TableQuery) => ['receipts', 'list', filters] as const,
    detail: (id: string) => ['receipts', 'detail', id] as const,
    batches: (id: string) => ['receipts', 'batches', id] as const,
    movements: (id: string) => ['receipts', 'movements', id] as const,
  },
  shipments: {
    all: () => ['shipments'] as const,
    list: (filters: TableQuery) => ['shipments', 'list', filters] as const,
    detail: (id: string) => ['shipments', 'detail', id] as const,
    movements: (id: string) => ['shipments', 'movements', id] as const,
  },
  supervision: {
    all: () => ['supervision'] as const,
    visits: (day: string, agentId: string) => ['supervision', 'visits', day, agentId] as const,
    overdue: (agentId: string) => ['supervision', 'overdue', agentId] as const,
    anomalies: (day: string) => ['supervision', 'anomalies', day] as const,
  },
  orders: {
    all: () => ['orders'] as const,
    list: (filters: TableQuery) => ['orders', 'list', filters] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
    count: (filters: Record<string, unknown>) => ['orders', 'count', filters] as const,
    availability: (excludeOrder?: string) =>
      ['orders', 'availability', excludeOrder ?? ''] as const,
  },
  writeOffs: {
    all: () => ['writeOffs'] as const,
    list: (filters: TableQuery) => ['writeOffs', 'list', filters] as const,
    detail: (id: string) => ['writeOffs', 'detail', id] as const,
  },
  returns: {
    all: () => ['returns'] as const,
    list: (filters: TableQuery) => ['returns', 'list', filters] as const,
    detail: (id: string) => ['returns', 'detail', id] as const,
  },
  adjustments: {
    all: () => ['adjustments'] as const,
    list: (filters: TableQuery) => ['adjustments', 'list', filters] as const,
  },
  storeSummary: (storeId: string) => ['stores', 'summary', storeId] as const,
  productBatches: (productId: string) => ['stock', 'batches', productId] as const,
  stock: {
    all: () => ['stock'] as const,
    list: (filters: TableQuery) => ['stock', 'list', filters] as const,
    quarantine: () => ['stock', 'quarantine'] as const,
  },
  stores: {
    assignments: (id: string) => ['stores', id, 'assignments'] as const,
    all: () => ['stores'] as const,
    list: (filters: TableQuery) => ['stores', 'list', filters] as const,
    one: (id: string) => ['stores', 'one', id] as const,
    // Under `stores`, so a decision that invalidates the shops refreshes what is still waiting.
    locationProposals: (status = 'PENDING') => ['stores', 'location-proposals', status] as const,
  },
  dashboard: {
    all: () => ['dashboard'] as const,
    /** Cards keyed individually so the period selector refetches only what depends on it. */
    card: (name: string, period?: Record<string, unknown>) =>
      ['dashboard', name, period ?? {}] as const,
  },
  reports: {
    all: () => ['reports'] as const,
    one: (name: string, params?: Record<string, unknown>) =>
      ['reports', name, params ?? {}] as const,
  },
  expenses: {
    all: () => ['expenses'] as const,
    list: (filters: Record<string, unknown>) => ['expenses', 'list', filters] as const,
    totals: (filters: Record<string, unknown>) => ['expenses', 'totals', filters] as const,
    categories: () => ['expenses', 'categories'] as const,
  },
  company: {
    all: () => ['company'] as const,
    profile: () => ['company', 'profile'] as const,
  },
  employees: {
    schemes: (id: string) => ['employees', 'schemes', id] as const,
    breakdown: (id: string, period: string) => ['employees', 'breakdown', id, period] as const,
    all: () => ['employees'] as const,
    list: (filters: TableQuery) => ['employees', 'list', filters] as const,
    lookup: () => ['employees', 'lookup'] as const,
    score: (id: string) => ['employees', 'score', id] as const,
  },
  payrolls: {
    all: () => ['payrolls'] as const,
    list: (filters: Record<string, unknown>) => ['payrolls', 'list', filters] as const,
  },
  finance: {
    all: () => ['finance'] as const,
    payments: (filters: Record<string, unknown>) => ['finance', 'payments', filters] as const,
    paymentTotals: (filters: Record<string, unknown>) =>
      ['finance', 'payments', 'totals', filters] as const,
    partySummary: (partyType: string, id: string) => ['finance', 'summary', partyType, id] as const,
    statement: (partyType: string, id: string, range: Record<string, unknown>) =>
      ['finance', 'statement', partyType, id, range] as const,
    agentStores: (id: string) => ['finance', 'agent', id, 'stores'] as const,
    agentPerformance: (id: string) => ['finance', 'agent', id, 'performance'] as const,
    supplierProducts: (id: string) => ['finance', 'supplier', id, 'products'] as const,
    aging: (agentId?: string) => ['finance', 'aging', agentId ?? 'all'] as const,
    goLive: () => ['finance', 'go-live'] as const,
    goLiveSummary: () => ['finance', 'go-live', 'summary'] as const,
  },
} as const
